import React, { ReactNode } from "react";
import styled from "styled-components";
import {
  Image,
  ImageProps,
  InfoCTA,
  InfoCTAProps,
  LinkNext,
  PageSection,
  PageSectionClassName,
  PageSectionProps,
  Theme,
  typography,
} from "@literati/public-ui-shared-react";
import analytics from "analytics";

/** This is the className that will be pre-pended to all of TeacherPartnerSectionMissionStatement's inner component classNames */
export const TEACHER_PARTNER_SECTION_MISSION_STATEMENT_CLASS_BASE =
  "teacher-partner-section-mission-statement";

/** These are all of the internal classNames that are used within the TeacherPartnerSectionMissionStatement component */
export const TeacherPartnerSectionMissionStatementClassName = {
  BASE: TEACHER_PARTNER_SECTION_MISSION_STATEMENT_CLASS_BASE,
  INFO_CTA_CONTAINER: `${TEACHER_PARTNER_SECTION_MISSION_STATEMENT_CLASS_BASE}-info-cta-container`,
} as const;

export interface TeacherPartnerSectionMissionStatementProps
  extends Omit<PageSectionProps, "children"> {
  className?: string;
  ctaLabel: ReactNode;
  ctaLink: string;
  imageProps?: ImageProps;
  infoCTAProps: InfoCTAProps;
}

const StyledPageSection = styled(PageSection)`
  .${PageSectionClassName.CONTENT_SPACING_CHILDREN} {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 3rem;
  }

  ${({ theme }) => theme.media.lg} {
    .${PageSectionClassName.CONTENT_SPACING_CHILDREN} {
      flex-direction: row;
      justify-content: center;
      text-align: left;
      gap: 0;
    }
  }
`;

const InfoCTAContainer = styled.div`
  flex-basis: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;

  ${({ theme }) => theme.media.lg} {
    align-items: flex-start;
  }
`;

const StyledInfoCTA = styled(InfoCTA)`
  .info-cta-description {
    font-size: 1.25rem;
  }
`;

const StyledLinkNext = styled(LinkNext)`
  margin: 3rem 0 0;
`;

const StyledImage = styled(Image)`
  flex-basis: 50%;
  display: flex;
  justify-content: center;
`;

const Eyebrow = styled.p`
  ${typography.eyebrow}
`;

const imageDefault = {
  alt: "3 pictures of children reading books.",
  height: 596,
  src: "https://images.ctfassets.net/vqtz4cq2w5ts/1SoDRd4K28zSRrKPf0qf29/a829b28ae4a1e9f8106b828fa0571006/Teacher_Partner_LP_OurMission_2x.png",
  width: 364,
};

export const missionStatementPropsDefaults: InfoCTAProps = {
  borderColor: "newsprint4",
  children: (
    <>
      <p>
        Literati is here to inspire every child to become a reader. We are a
        women-led company that believes reading is essential to helping children
        reach their full potential. In that spirit, we&apos;ve donated over half
        a million books to schools and communities in need.
      </p>
      <br />
      <p>
        With this program, we hope to support teachers in encouraging a love of
        reading by matching every child with the perfect books—helping them to
        grow and become lifelong readers.
      </p>
    </>
  ),
  primaryHeading: (
    <>
      <Eyebrow>OUR GOAL</Eyebrow>
      <em>Together</em>, we&apos;ll inspire a lifelong love of reading
    </>
  ),
};

export const TeacherPartnerSectionMissionStatement = ({
  className = "",
  ctaLabel,
  ctaLink,
  imageProps = imageDefault,
  infoCTAProps,
  ...pageSectionProps
}: TeacherPartnerSectionMissionStatementProps) => {
  const cls = TeacherPartnerSectionMissionStatementClassName;

  const handleLinkClick = () =>
    analytics.trackAmplitudeClickEvent(
      "Get Started",
      "TeacherPartnerSectionMissionStatement"
    );

  return (
    <Theme>
      <StyledPageSection
        bgColor="periwinkleTint5"
        className={`${cls.BASE} ${className}`}
        {...pageSectionProps}
      >
        <InfoCTAContainer className={cls.INFO_CTA_CONTAINER}>
          <StyledInfoCTA {...infoCTAProps} />
          <StyledLinkNext
            href={ctaLink}
            legacyBehavior={false}
            onClick={handleLinkClick}
            variant="button"
          >
            {ctaLabel}
          </StyledLinkNext>
        </InfoCTAContainer>
        <StyledImage contain {...imageProps} />
      </StyledPageSection>
    </Theme>
  );
};

export default TeacherPartnerSectionMissionStatement;
